import { useState } from 'react';
import { Check, Copy, QrCode as QrIcon } from 'lucide-react';
import { formatDateTime } from 'helpers/formatters';
import { previewLinks, withScheme } from 'helpers/preview-link';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { QrCode } from '@/components/QrCode';
import { usePreviewSignatures } from '@/state/usePreviewSignature';

type PreviewVariant = { id: number; name: string };

type QaPreviewPanelProps = {
  experimentId: number;
  /** When QA ends; the scheduler returns the experiment to Draft then. */
  qaExpiresAt: number | null;
  variants: readonly PreviewVariant[];
  /** The page to start from: the experiment's editor page, or the website's address. */
  defaultPageUrl: string;
};

/**
 * The preview links of an experiment in QA, one per variant: open one on any device to see that
 * variant on the live site. Built from a page anyone can change here, since the dashboard does
 * not know which page the variant is for; targeting still decides where it shows.
 */
export const QaPreviewPanel = ({ experimentId, qaExpiresAt, variants, defaultPageUrl }: QaPreviewPanelProps) => {
  // Null until someone types: until then the field follows the default, which may only arrive
  // once the website has loaded.
  const [typedPageUrl, setTypedPageUrl] = useState<string | null>(null);
  const pageUrl = typedPageUrl ?? defaultPageUrl;
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [qrFor, setQrFor] = useState<number | null>(null);

  // The panel shows only for an experiment in QA; signed links mark their preview reports (PER-333).
  const signatures = usePreviewSignatures(experimentId, true);
  const rows = variants.map((variant) => ({
    variant,
    links: previewLinks({
      pageUrl: withScheme(pageUrl),
      experimentId,
      variantId: variant.id,
      signature: signatures[variant.id],
    }),
  }));
  const reset = rows.find((row) => row.links)?.links?.reset;

  const copy = async (variantId: number, link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(variantId);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (ex) {
      console.error('copying the QA link failed', ex);
    }
  };

  return (
    <section className="bg-panel border-line rounded-panel border p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="micro-label">qa preview</p>
        {qaExpiresAt && <p className="supporting">QA ends {formatDateTime(qaExpiresAt)}</p>}
      </div>
      <p className="supporting mt-1">
        Open a link on any device to see that variant on the live site. Visitors never see it and nothing is tracked.
      </p>

      <label className="micro-label mt-3 flex flex-col gap-1.5">
        Page
        <Input
          value={pageUrl}
          onChange={(ev) => setTypedPageUrl(ev.target.value)}
          placeholder="https://"
          className="border-line bg-field text-foreground h-8 rounded-lg font-mono text-nano"
        />
      </label>

      <ul className="mt-3 flex flex-col gap-2">
        {rows.map(({ variant, links }) => (
          <li key={variant.id} className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-foreground w-32 shrink-0 truncate text-meta" title={variant.name}>
                {variant.name}
              </span>
              {links ? (
                <>
                  <Input
                    readOnly
                    aria-label={`QA link for ${variant.name}`}
                    value={links.preview}
                    onFocus={(ev) => ev.target.select()}
                    className="border-line bg-field text-foreground h-8 min-w-0 flex-1 rounded-lg font-mono text-nano"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={copiedId === variant.id ? 'Copied' : `Copy the QA link for ${variant.name}`}
                    onClick={() => copy(variant.id, links.preview)}
                    className="text-dim hover:text-foreground hover:bg-hover size-8 shrink-0 rounded-lg"
                  >
                    {copiedId === variant.id ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`QR code for ${variant.name}`}
                    aria-pressed={qrFor === variant.id}
                    onClick={() => setQrFor(qrFor === variant.id ? null : variant.id)}
                    className="text-dim hover:text-foreground hover:bg-hover size-8 shrink-0 rounded-lg"
                  >
                    <QrIcon className="size-3.5" />
                  </Button>
                </>
              ) : (
                <span className="supporting">Enter the address of a page on the site.</span>
              )}
            </div>
            {links && qrFor === variant.id && (
              <div className="flex w-fit rounded-lg bg-white p-2">
                <QrCode value={links.preview} label={`QR code for the ${variant.name} QA link`} />
              </div>
            )}
          </li>
        ))}
      </ul>

      {reset && (
        <p className="supporting mt-3">
          A browser that opened a link keeps the preview until QA ends. To go back to normal there, open{' '}
          <a href={reset} target="_blank" rel="noopener" className="text-accent underline">
            the reset link
          </a>
          .
        </p>
      )}
    </section>
  );
};
